import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ColumnType } from "antd/es/table/interface";
import { App, Flex, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { EllipsisOutlined, ShareAltOutlined } from "@ant-design/icons";

import QueryTable from "@/components_v2/Tables/QueryTable";
import {
  commonsApi,
  useGetCommunityRolesQuery,
  useLazyGetPasswordResetLinkQuery,
  useGetAreasOfExpertiseQuery,
  useLazyGetUsersQuery,
  useLazyImpersonateUserQuery,
} from "@/redux/service";
import { Roles } from "@/shared/utils/constants";
import { CommunityRoleResponse } from "@/redux/common/types";
import { BaseUserResponse, ExpertiseAreaResponse } from "@/redux/users/types";
import { UserFiltersState, UserTableProps } from "@/features/users/list/types";
import UserFilters from "@/features/users/list/components/UserFilters";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { setAuthenticationState } from "@/redux/authentication/reducer";
import { LocalStorageName } from "@/redux/api";
import BaseButton from "@/components_v2/Button/BaseButton";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import { useUserPermissions } from "@/features/users/hooks/useUserPermissions";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import EyeOffIcon from "@/assets/icons/eye-off.svg?react";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import { isPaginatedResponse } from "@/redux/typeGuards";
import BaseTag from "@/components_v2/Tags/BaseTag";

const { Text } = Typography;

const UserActionsCell = ({ user }: { user: BaseUserResponse }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const currentUser = useSelector(selectCurrentUser);
  const [impersonateUser] = useLazyImpersonateUserQuery();
  const [getResetPasswordLink] = useLazyGetPasswordResetLinkQuery();
  const { canImpersonate, canCopyResetPasswordLink } = useUserPermissions(
    user,
    currentUser,
  );

  const handleImpersonateUser = (userId: number) => {
    impersonateUser(userId)
      .unwrap()
      .then((response) => {
        dispatch(commonsApi.util.resetApiState());
        dispatch(
          setAuthenticationState({
            isAuthenticated: true,
            user: response.user,
            isFirstLogin: false,
          }),
        );
        window.localStorage.setItem(
          LocalStorageName,
          JSON.stringify({
            accessExpiration: response.accessExpiration,
            refreshExpiration: response.refreshExpiration,
            user: response.user,
          }),
        );
        navigate("/");
      });
  };

  const handleCopyResetPasswordLink = (userId: number) => {
    getResetPasswordLink(userId)
      .unwrap()
      .then((response) => {
        navigator.clipboard.writeText(response.link).then(() => {
          notification.success({ message: t("users.labels.linkCopied") });
        });
      })
      .catch((error) => {
        const code = error.data.code;
        if (code === "user_no_password_reset_token") {
          notification.error({
            message: t("users.labels.userHasNoPasswordResetToken"),
          });
        } else if (code === "user_inactive") {
          notification.error({
            message: t("users.labels.userIsInactiveAndCannotBeImpersonated"),
          });
        }
      });
  };

  return (
    <>
      <DropdownMenu
        content={
          <div>
            {canImpersonate && (
              <BaseTooltip
                title={
                  !user.isActive
                    ? t("users.labels.userIsInactiveAndCannotBeImpersonated")
                    : ""
                }
              >
                <BaseButton
                  className="dropdown-menu-button"
                  isFullWidth
                  size={BUTTON_SIZES.SMALL}
                  disabled={!user.isActive}
                  variant={BUTTON_VARIANTS.LINK}
                  leadingIcon={<ShareAltOutlined />}
                  onClick={() => handleImpersonateUser(user.id)}
                >
                  {t("users.labels.impersonate")}
                </BaseButton>
              </BaseTooltip>
            )}
            {canCopyResetPasswordLink && (
              <BaseTooltip
                title={
                  !user.isActive
                    ? t("users.labels.userIsInactiveAndCannotResetPassword")
                    : ""
                }
              >
                <BaseButton
                  className="dropdown-menu-button"
                  isFullWidth
                  size={BUTTON_SIZES.SMALL}
                  disabled={!user.isActive}
                  variant={BUTTON_VARIANTS.LINK}
                  leadingIcon={<ShareAltOutlined />}
                  onClick={() => handleCopyResetPasswordLink(user.id)}
                >
                  {t("users.labels.copyResetPasswordLink")}
                </BaseButton>
              </BaseTooltip>
            )}
          </div>
        }
        placement="bottom"
      >
        <BaseButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.LINK}
          leadingIcon={<EllipsisOutlined />}
        />
      </DropdownMenu>
    </>
  );
};

const UserTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  additionalColumns,
  filtersCallback,
  ...props
}: UserTableProps<BaseUserResponse>) => {
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const [filters, setFilters] =
    useState<Partial<UserFiltersState>>(defaultFilters);

  const { data: communityRoles } = useGetCommunityRolesQuery({
    pagination: "off",
    type: "profile",
  });
  const { data: expertiseAreas } = useGetAreasOfExpertiseQuery({
    ids: [],
    pagination: "off",
  });

  const communityRolesOptions = useMemo(() => {
    if (!communityRoles) return {};
    const data = isPaginatedResponse(communityRoles)
      ? communityRoles.results
      : communityRoles;
    return data.reduce<Record<number, CommunityRoleResponse>>((acc, role) => {
      acc[role.id] = role;
      return acc;
    }, {});
  }, [communityRoles]);

  const expertiseAreasOptions = useMemo(() => {
    if (!expertiseAreas) return {};
    const data = isPaginatedResponse(expertiseAreas)
      ? expertiseAreas.results
      : expertiseAreas;
    return data.reduce<Record<number, ExpertiseAreaResponse>>((acc, area) => {
      acc[area.id] = area;
      return acc;
    }, {});
  }, [expertiseAreas]);
  const columns: Record<string, ColumnType<BaseUserResponse>> = useMemo(() => {
    if (!currentUser) return {};

    const predefinedColumns: Record<string, ColumnType<BaseUserResponse>> = {
      fullName: {
        title: t("users.labels.fullName"),
        dataIndex: "fullName",
        ellipsis: true,
        width: currentUser.role === Roles.ADMIN.value ? 250 : 350,
        sorter: true,
        defaultSortOrder: "ascend",
        render: (value: string, user) => {
          return (
            <Text
              style={{
                maxWidth: currentUser.role === Roles.ADMIN.value ? 250 : 350,
              }}
              ellipsis={{
                symbol: "...",
                tooltip: true,
              }}
            >
              <Link to={`/users/${user.id}`}>{value}</Link>
            </Text>
          );
        },
      },
      email: {
        title: t("users.labels.email"),
        key: "email",
        dataIndex: "email",
        render: (value: string) => {
          return (
            value || (
              <span className="flex items-center gap-100">
                <BaseSvgRenderer
                  component={EyeOffIcon}
                  width="16"
                  height="16"
                  className="text-gray-600"
                />
                {t("users.labels.emailHidden")}
              </span>
            )
          );
        },
      },
      organization: {
        title: t("users.labels.organization"),
        key: "organization",
        dataIndex: "organization",
        width: 250,
        sorter: true,
        render: (value) => {
          if (!value) return;

          return (
            <Text
              style={{
                maxWidth: 250,
              }}
              ellipsis={{
                symbol: "...",
                tooltip: true,
              }}
            >
              <Link to={`/signatories/${value.id}`}>{value.name}</Link>
            </Text>
          );
        },
      },
      jobTitle: {
        title: t("users.labels.jobTitle"),
        key: "jobTitle",
        dataIndex: ["profile", "jobTitle"],
      },
      communityRoles: {
        title: t("users.labels.communityRoles"),
        key: "communityRoles",
        dataIndex: ["profile", "communityRoles"],
        render: (communityRoles: number[]) => {
          if (!communityRoles || communityRoles.length === 0) {
            return "—";
          }

          return (
            <div className="flex gap-100">
              {communityRoles.map((roleId) => {
                const role = communityRolesOptions[roleId];
                if (!role || !role.icon) return null;

                return (
                  <BaseTooltip title={role.label} key={roleId}>
                    <img src={role.icon} alt={role.label} />
                  </BaseTooltip>
                );
              })}
            </div>
          );
        },
      },
      expertiseAreas: {
        title: t("users.labels.expertiseAreas"),
        key: "expertiseAreas",
        dataIndex: ["profile", "areasOfExpertise"],
        render: (expertiseAreas: number[]) => {
          if (!expertiseAreas || expertiseAreas.length === 0) {
            return "—";
          }

          const validAreas = expertiseAreas
            .map((areaId) => {
              const area = expertiseAreasOptions[areaId];
              return area && area.name ? { id: areaId, name: area.name } : null;
            })
            .filter(
              (area): area is { id: number; name: string } => area !== null,
            );

          if (validAreas.length === 0) {
            return "—";
          }

          const firstArea = validAreas[0];
          const remainingAreas = validAreas.slice(1);

          return (
            <div className="flex gap-100">
              <BaseTag size="small" label={firstArea?.name ?? ""} />
              {remainingAreas.length > 0 && (
                <BaseTooltip
                  title={
                    <div className="flex flex-col gap-050">
                      {remainingAreas.map((area) => (
                        <span key={area.id}>{area.name}</span>
                      ))}
                    </div>
                  }
                >
                  <BaseTag size="small" label={`+${remainingAreas.length}`} />
                </BaseTooltip>
              )}
            </div>
          );
        },
      },
      actions: {
        title: t("organizations.labels.actions"),
        key: "actions",
        render: (user: BaseUserResponse) => <UserActionsCell user={user} />,
      },
    };
    return { ...predefinedColumns, ...(additionalColumns || {}) };
  }, [currentUser, t, communityRolesOptions, expertiseAreasOptions]);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) => columns[columnKey] as ColumnType<BaseUserResponse>,
      ),
    [columns, columnNames],
  );

  return (
    <Flex vertical gap="small">
      <UserFilters
        filterNames={filterNames}
        setFilters={(data) => {
          setFilters(data);
          if (filtersCallback) filtersCallback(data);
        }}
        defaultFilters={defaultFilters}
      />
      <QueryTable<BaseUserResponse>
        query={useLazyGetUsersQuery}
        filters={filters}
        initialSorting={{
          field: "fullName",
          order: "descend",
        }}
        columns={mappedColumns}
        {...props}
      />
    </Flex>
  );
};

export default UserTable;
